'use client';

import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import {
  BorderedNavigationMenu,
  BorderedNavigationMenuItem,
} from '@kit/ui/bordered-navigation-menu';
import MobileNavigationDropdown from '@kit/ui/mobile-navigation-dropdown';

import { useRecordTerminology } from '../../hooks/use-record-terminology';
import { recordManageRoot } from '../../lib/record-paths';
import { type RecordType } from '../../lib/record-terminology';

interface RecordShellProps {
  accountSlug: string;
  auditionId: string;
  recordType: RecordType;
  /**
   * Whether the audition has ≥1 accepted application. Drives Offers
   * visibility for auditions (competitions always show Offers). Computed
   * server-side in the layout via a head/count query.
   */
  hasAcceptedApplications: boolean;
}

interface NavItemSpec {
  /** Sub-route segment relative to the record root. */
  segment: string;
  /** i18n key under `auditions:manage.nav.*`. */
  labelKey: string;
  /** Visible for these record types. */
  visibleFor: RecordType[];
  /** Extra predicate (e.g. Offers for auditions only when accepted apps). */
  enabled?: (props: RecordShellProps) => boolean;
  /** End-active so a tab doesn't stay highlighted on deeper subroutes. */
  end?: boolean;
}

/**
 * Single source of nav-item visibility for the record shell. Competition-only
 * items simply declare `visibleFor: ['competition']`. Wording is resolved via
 * `useRecordTerminology` for the forking Applications→Entries label; the rest
 * read their `manage.nav.*` key directly.
 *
 * Settings-first IA (2026-07-02, P1/P2): the Overview tab is gone (the record
 * root redirects to Details/"Settings"), and Directors is merged into Partners
 * (org-level access only, no person-level competition invites). Remaining
 * competition tabs: Settings, Entries, Partners, Offers, Meetings.
 *
 * Mirrors the visibility matrix in the WIP Goal Contract (R2) — partners and
 * meetings are competition-only; offers is competition-always /
 * audition-when-accepted.
 */
const NAV_ITEMS: NavItemSpec[] = [
  {
    segment: 'details',
    labelKey: 'auditions:manage.nav.details',
    visibleFor: ['audition', 'competition'],
  },
  // BAL-386: Categories, Application form and Offer types moved out of the top
  // nav into the unified Settings sidebar (reached via the Details/"Settings"
  // tab). The top nav is now operational-only + Settings.
  {
    // Context-forked label (Applications → Entries) resolved via terminology.
    segment: 'applications',
    labelKey: 'auditions:manage.nav.applications',
    visibleFor: ['audition', 'competition'],
  },
  {
    // Read-only candidate grid/table (BAL-563) — promoted from a
    // `?view=participants` toggle on Entries to its own tab (2026-07-05).
    segment: 'participants',
    labelKey: 'auditions:adminParticipants.title',
    visibleFor: ['competition'],
  },
  {
    segment: 'partners',
    labelKey: 'auditions:manage.nav.partners',
    visibleFor: ['competition'],
  },
  {
    segment: 'offers',
    labelKey: 'auditions:manage.nav.offers',
    visibleFor: ['audition', 'competition'],
    enabled: (p) => p.recordType === 'competition' || p.hasAcceptedApplications,
  },
  {
    segment: 'meetings',
    labelKey: 'auditions:manage.nav.meetings',
    visibleFor: ['competition'],
  },
];

export function RecordShell(props: RecordShellProps) {
  const { accountSlug, auditionId, recordType } = props;
  const pathname = usePathname();
  const { t } = useRecordTerminology(recordType);
  const { t: tRaw } = useTranslation('auditions');

  const root = recordManageRoot(accountSlug, recordType, auditionId);

  const items = NAV_ITEMS.filter(
    (item) =>
      item.visibleFor.includes(recordType) &&
      (item.enabled ? item.enabled(props) : true),
  ).map((item) => {
    const path = item.segment ? `${root}/${item.segment}` : root;
    // The Applications item forks its label (Applications → Entries);
    // everything else uses its plain nav key.
    const label =
      item.segment === 'applications' ? t('applications') : undefined;
    return { ...item, path, label };
  });

  // Mobile dropdown labels: resolve forked Applications label, plain keys
  // otherwise. The dropdown reads `label` as an i18n key via <Trans>, so we
  // pass the already-resolved string for the forked item and the key for the
  // rest (Trans renders a bare string as its own value).
  const mobileLinks = items.map((item) => ({
    path: item.path,
    label: item.label ?? item.labelKey,
  }));

  return (
    <nav
      aria-label={tRaw('recordShell.sectionsAria')}
      // Horizontal inset matches the breadcrumb + identity strip above
      // (px-4 / lg:px-8) so the tabs align with the rest of the record header
      // instead of sitting flush against the viewport edge. The border-b still
      // spans full width (border draws at the box edge, outside the padding).
      className="border-border/60 border-b px-4 lg:px-8"
    >
      {/* Desktop: shared underline tab bar (@kit/ui/bordered-navigation-menu —
          flush coral underline on active, no pill). Same canonical component
          the marketplace header uses. */}
      <div className="hidden md:block">
        <BorderedNavigationMenu>
          {items.map((item) => (
            <BorderedNavigationMenuItem
              key={item.path}
              path={item.path}
              label={item.label ?? item.labelKey}
              end={item.end}
            />
          ))}
        </BorderedNavigationMenu>
      </div>

      {/* Mobile: dropdown fed from the same computed item list. */}
      <div className="py-2 md:hidden">
        <MobileNavigationDropdown links={mobileLinks} currentPath={pathname} />
      </div>
    </nav>
  );
}
